// src/hospital/HospitalRoutes.jsx
// React Router v6 routes for all 27 Hospital Management pages
// Usage: Import this inside your main dashboard router
// ─────────────────────────────────────────────────────────────────────────────
import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import {
  PatientRegistrationPage,
  PatientRecordsPage,
  AppointmentsPage,
  DoctorsPage,
  NursesPage,
  DepartmentsPage,
  WardsPage,
  BedManagementPage,
  OPDPage,
  IPDPage,
  EmergencyPage,
  LaboratoryPage,
  PathologyPage,
  RadiologyPage,
  PharmacyPage,
  BloodBankPage,
  OperationTheaterPage,
  AmbulancePage,
  ICUPage,
  MaternityPage,
  HospitalBillingPage,
  InsurancePage,
  MortalityPage,
  PrescriptionsPage,
  VitalSignsPage,
  DietNutritionPage,
} from '../pages/hospital/hospitalPages';

// Medical Records page (same as PatientRecordsPage but on /medical-records path)
import { medicalRecordController } from '../controllers/hospitalController';

// ─────────────────────────────────────────────────────────────────────────────
// Loading fallback
// ─────────────────────────────────────────────────────────────────────────────
const PageLoader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 12 }}>
    <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTop: '3px solid #0f4c81', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <div style={{ fontSize: 13, color: '#64748b' }}>Loading...</div>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// HOSPITAL ROUTES COMPONENT
// Props:
//   companyId  — required, passed to every page for data scoping
//   basePath   — default: '/dashboard/hospital'
// ─────────────────────────────────────────────────────────────────────────────
const HospitalRoutes = ({ companyId, basePath = '/dashboard/hospital' }) => {

  // Helper — wrap every page with companyId prop
  const page = (Component) => <Component companyId={companyId} />;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Default redirect */}
        <Route index element={<Navigate to="patients/register" replace />} />

        {/* 1. Patient Registration */}
        <Route path="patients/register"  element={page(PatientRegistrationPage)} />

        {/* 2. Patient Records */}
        <Route path="patients/records"   element={page(PatientRecordsPage)} />

        {/* 3. Appointments */}
        <Route path="appointments"       element={page(AppointmentsPage)} />

        {/* 4. Doctors */}
        <Route path="doctors"            element={page(DoctorsPage)} />

        {/* 5. Nurses */}
        <Route path="nurses"             element={page(NursesPage)} />

        {/* 6. Departments */}
        <Route path="departments"        element={page(DepartmentsPage)} />

        {/* 7. Wards & Rooms */}
        <Route path="wards"              element={page(WardsPage)} />

        {/* 8. Bed Management */}
        <Route path="beds"               element={page(BedManagementPage)} />

        {/* 9. OPD Management */}
        <Route path="opd"                element={page(OPDPage)} />

        {/* 10. IPD Management */}
        <Route path="ipd"                element={page(IPDPage)} />

        {/* 11. Emergency */}
        <Route path="emergency"          element={page(EmergencyPage)} />

        {/* 12. Laboratory */}
        <Route path="laboratory"         element={page(LaboratoryPage)} />

        {/* 13. Pathology */}
        <Route path="pathology"          element={page(PathologyPage)} />

        {/* 14. Radiology */}
        <Route path="radiology"          element={page(RadiologyPage)} />

        {/* 15. Pharmacy */}
        <Route path="pharmacy"           element={page(PharmacyPage)} />

        {/* 16. Blood Bank */}
        <Route path="blood-bank"         element={page(BloodBankPage)} />

        {/* 17. Operation Theater */}
        <Route path="operation-theater"  element={page(OperationTheaterPage)} />

        {/* 18. Ambulance Service */}
        <Route path="ambulance"          element={page(AmbulancePage)} />

        {/* 19. ICU Management */}
        <Route path="icu"                element={page(ICUPage)} />

        {/* 20. Maternity Ward */}
        <Route path="maternity"          element={page(MaternityPage)} />

        {/* 21. Hospital Billing */}
        <Route path="billing"            element={page(HospitalBillingPage)} />

        {/* 22. Insurance Claims */}
        <Route path="insurance"          element={page(InsurancePage)} />

        {/* 23. Medical Records */}
        <Route path="medical-records"    element={page(PatientRecordsPage)} />

        {/* 24. Prescriptions */}
        <Route path="prescriptions"      element={page(PrescriptionsPage)} />

        {/* 25. Vital Signs */}
        <Route path="vital-signs"        element={page(VitalSignsPage)} />

        {/* 26. Diet & Nutrition */}
        <Route path="diet-nutrition"     element={page(DietNutritionPage)} />

        {/* 27. Mortality Records */}
        <Route path="mortality"          element={page(MortalityPage)} />

        {/* 404 fallback */}
        <Route path="*" element={<Navigate to="patients/register" replace />} />
      </Routes>
    </Suspense>
  );
};

export default HospitalRoutes;